import { FREE_CELL, isFree, PlanCell, PlanColumn, PlanRow } from "./plan-types";
import { readQueue, toQueueInsertIndex, writeQueue } from "./plan-queue";
import { queueIndexes, releaseAbsence } from "./plan-operations";
import { PlanGrid, slotsFrom } from "./plan-scale";

/**
 * Команды каскадного сдвига.
 *
 * Обычное перетаскивание соседей не двигает — это осознанное решение (см.
 * plan-operations.ts). Но иногда сдвинуть хвост действительно нужно: работы
 * начались позже, между этапами добавился ещё один. Такие правки выполняются
 * этими командами — явно, из контекстного меню, а не как побочный эффект мыши.
 *
 * Дни ведут себя как элементы списка: вставка раздвигает (блок, в середину
 * которого попали, разрезается), удаление подтягивает хвост влево.
 */

/** Довести длину очереди до исходной, обрезав только свободный хвост */
function fitLength(cells: PlanCell[], total: number): boolean {
    while (cells.length < total) {
        cells.push(FREE_CELL);
    }

    if (cells.length > total) {
        if (!cells.slice(total).every(isFree)) {
            return false;
        }

        cells.length = total;
    }

    return true;
}

/** Вставить дни, раздвинув всё, что стоит правее */
export function insertWithShift(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    at: number,
    payload: PlanCell[]
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || !payload.length) {
        return null;
    }

    const queue = readQueue(row, columns);
    const total = queue.cells.length;
    const cells = queue.cells.slice();
    const insertAt = Math.max(0, Math.min(toQueueInsertIndex(queue, at), total));

    cells.splice(insertAt, 0, ...payload);

    if (!fitLength(cells, total)) {
        return null;
    }

    const result = rows.slice();
    result[rowIndex] = writeQueue(row, queue, cells);

    return result;
}

/**
 * Вставить ячейки со сдвигом — недельный вариант `insertWithShift`.
 *
 * Единица вставки здесь ячейка, а не день: сколько недель скопировали, столько
 * и раздвинется. Содержимое группы подгоняется под длину целевой недели так же,
 * как в `pasteSlots` (лишние дни отбрасываются, последний растягивается), поэтому
 * вставка всегда закрывает ячейку целиком, а не её кусок в пять дней.
 */
export function insertSlotsWithShift(
    rows: PlanRow[],
    columns: PlanColumn[],
    grid: PlanGrid,
    rowIndex: number,
    at: number,
    payload: PlanCell[][]
): PlanRow[] | null {
    const row = rows[rowIndex];

    if (!row || !payload.length) {
        return null;
    }

    const targets = slotsFrom(grid, at, payload.length);

    if (!targets) {
        return null;
    }

    const queue = readQueue(row, columns);
    const cells: PlanCell[] = [];

    targets.forEach((slot, index) => {
        const group = payload[index];
        // в очередь попадают не все дни недели: отпуск и прошлое закреплены за датой
        const size = queueIndexes(queue, slot.start, slot.length).length;

        for (let day = 0; day < size; day++) {
            cells.push(group && group.length ? group[Math.min(day, group.length - 1)] : FREE_CELL);
        }
    });

    if (!cells.length) {
        return null;
    }

    return insertWithShift(rows, columns, rowIndex, targets[0].start, cells);
}

/** Удалить дни, подтянув хвост влево */
export function deleteWithShift(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    start: number,
    length: number
): PlanRow[] | null {
    const source = rows[rowIndex];

    if (!source || length < 1) {
        return null;
    }

    // дни отсутствия в очередь не входят — сначала снимаем их, тогда освободившиеся
    // даты становятся частью очереди и хвост подтягивается на них так же, как на работы
    const row = releaseAbsence(source, columns, start, length);

    const queue = readQueue(row, columns);
    const picked = queueIndexes(queue, start, length);

    if (!picked.length) {
        return null;
    }

    const total = queue.cells.length;
    const cells = queue.cells.slice();

    cells.splice(picked[0], picked.length);
    fitLength(cells, total);

    const result = rows.slice();
    result[rowIndex] = writeQueue(row, queue, cells);

    return result;
}

/** Сдвинуть всё, начиная с колонки, на указанное число рабочих дней вправо */
export function shiftTailRight(
    rows: PlanRow[],
    columns: PlanColumn[],
    rowIndex: number,
    from: number,
    days: number
): PlanRow[] | null {
    if (days < 1) {
        return null;
    }

    const payload: PlanCell[] = [];

    for (let i = 0; i < days; i++) {
        payload.push(FREE_CELL);
    }

    return insertWithShift(rows, columns, rowIndex, from, payload);
}
